
import { useState, useEffect } from 'react';
import { MapPin, Truck, BriefcaseMedical, Home, Users, Clock, Shield, Zap } from 'lucide-react';

const FractionalNursing = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [rotation, setRotation] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('fractional-nursing');
      if (element) {
        const position = element.getBoundingClientRect();
        if (position.top < window.innerHeight * 0.75) {
          setIsVisible(true);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Animation for the orbiting icon
  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setRotation(prev => (prev + 1) % 360);
      }, 50);
      
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  return (
    <section id="fractional-nursing" className="py-20">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className={`text-3xl md:text-4xl font-bold text-abbella-ebony mb-4 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
            The Fractional Nursing Revolution
          </h2>
          <p className={`text-lg text-slate-600 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
            Rethinking how care is delivered through our innovative CareBase model
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Visual representation */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0 translate-x-8'}`}>
            <div className="bg-white rounded-xl shadow-xl p-6 relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-abbella-aqua/10"></div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-abbella-aqua/10"></div>
              
              <div className="relative z-10">
                <div className="w-full max-w-md mx-auto relative">
                  {/* Central hub - Orbiting icon */}
                  <div 
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 md:w-32 md:h-32 bg-abbella-aqua rounded-full flex items-center justify-center shadow-lg z-20"
                    style={{ transform: `translate(-50%, -50%) rotate(${rotation}deg)` }}
                  >
                    <div className="text-white text-center" style={{ transform: `rotate(-${rotation}deg)` }}>
                      <p className="font-bold md:text-lg">CareBase</p>
                    </div>
                  </div>
                  
                  {/* Concentric circles representing coverage area */}
                  <div className="w-full aspect-square rounded-full border-2 border-dashed border-abbella-aqua/30 flex items-center justify-center">
                    <div className="w-3/4 aspect-square rounded-full border-2 border-dashed border-abbella-aqua/50 flex items-center justify-center">
                      <div className="w-1/2 aspect-square rounded-full border-2 border-dashed border-abbella-aqua/70">
                      </div>
                    </div>
                  </div>
                  
                  {/* Caregiver icons on the outer circle */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-blue-200">
                    <div className="text-abbella-ebony text-xs text-center">
                      <Users size={16} className="mx-auto mb-1 text-blue-500" />
                      Caregiver
                    </div>
                  </div>
                  
                  <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 w-14 h-14 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-blue-200">
                    <div className="text-abbella-ebony text-xs text-center">
                      <Users size={16} className="mx-auto mb-1 text-blue-500" />
                      Caregiver
                    </div>
                  </div>
                  
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-14 h-14 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-blue-200">
                    <div className="text-abbella-ebony text-xs text-center">
                      <Users size={16} className="mx-auto mb-1 text-blue-500" />
                      Caregiver
                    </div>
                  </div>
                  
                  <div className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 w-14 h-14 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-blue-200">
                    <div className="text-abbella-ebony text-xs text-center">
                      <Users size={16} className="mx-auto mb-1 text-blue-500" />
                      Caregiver
                    </div>
                  </div>
                  
                  {/* Client icons in the middle ring (between outer and inner) */}
                  <div className="absolute top-[25%] left-[30%] w-14 h-14 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-green-200">
                    <div className="text-abbella-ebony text-xs text-center">
                      <Home size={16} className="mx-auto mb-1 text-green-500" />
                      Client
                    </div>
                  </div>
                  
                  <div className="absolute top-[30%] right-[30%] w-14 h-14 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-green-200">
                    <div className="text-abbella-ebony text-xs text-center">
                      <Home size={16} className="mx-auto mb-1 text-green-500" />
                      Client
                    </div>
                  </div>
                  
                  <div className="absolute bottom-[30%] right-[30%] w-14 h-14 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-green-200">
                    <div className="text-abbella-ebony text-xs text-center">
                      <Home size={16} className="mx-auto mb-1 text-green-500" />
                      Client
                    </div>
                  </div>
                  
                  <div className="absolute bottom-[30%] left-[35%] w-14 h-14 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-green-200">
                    <div className="text-abbella-ebony text-xs text-center">
                      <Home size={16} className="mx-auto mb-1 text-green-500" />
                      Client
                    </div>
                  </div>
                  
                  {/* Distance indicator */}
                  <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 text-sm text-abbella-ebony/70 bg-white px-2 py-1 rounded-full">
                    5-10 mile radius
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Explanation */}
          <div className={`transition-all duration-700 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0 translate-x-8'}`}>
            <h3 className="text-2xl font-bold text-abbella-ebony mb-6">How Our CareBase Model Works</h3>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-abbella-aqua/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-abbella-aqua" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-abbella-ebony mb-2">Local CareBases:</h4>
                  <p className="text-slate-600">
                    Caregivers work from nearby hubs, staying within 5-10 miles of clients to ensure quick, reliable service.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-abbella-aqua/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-abbella-aqua" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-abbella-ebony mb-2">Flexible Visits:</h4>
                  <p className="text-slate-600">
                    Care is provided in exactly the time needed (30 minutes, 1 hour, etc.) without forcing you to pay for unnecessary hours.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-abbella-aqua/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-abbella-aqua" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-abbella-ebony mb-2">Consistent Care Team:</h4>
                  <p className="text-slate-600">
                    Each client gets matched with up to 3 dedicated caregivers who get to know them personally, eliminating the problem of constantly rotating strangers.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-abbella-aqua/20 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-abbella-aqua" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-abbella-ebony mb-2">Tech-Powered Reliability:</h4>
                  <p className="text-slate-600">
                    Advanced scheduling and matching technology ensures caregivers arrive on time and no-shows are eliminated, giving families peace of mind.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FractionalNursing;
